import clsx from 'clsx'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import moment from 'moment'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { BaseRatePackageView } from 'selectors/offerPage/offerPageSelectors'
import { areRatesLoading, getBaseRatePackagesView, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import AlwaysOnPriceDetails from '../AlwaysOnPriceDetails'
import { INTL_DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import TextButton from 'LuxKit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import BodyText from 'LuxKit/Typography/BodyText'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import OfferBookButton from '../../OfferBookButtons/OfferBookButton'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import Caption from 'LuxKit/Typography/Caption'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import RenderInclusions from '../RenderInclusions'
import PackageInclusionsValidityText, { hasStructuredPackageInclusionsValidityText } from 'components/OfferPage/Accommodation/PackageInclusionsValidityText'
import { getInclusionsWithoutPackageValidityText } from 'components/OfferPage/Accommodation/packageInclusionValidityUtils'

import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import AccommodationPackageTile from 'components/AccommodationKit/AccommodationPackageTile'
import AccommodationPackageTileContentArea from 'components/AccommodationKit/AccommodationPackageTileContentArea'
import AccommodationPackageTilePricingArea from 'components/AccommodationKit/AccommodationPackageTilePricingArea'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock, { ACCOMMODATION_PACKAGE_TITLE } from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import OfferAddToCartButton from '../../OfferBookButtons/OfferAddToCartButton'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import OfferSelectPackageButton from '../../OfferBookButtons/OfferSelectPackageButton'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import TextLink from 'LuxKit/TextLink'
import GeoContext from 'contexts/geoContext'
import { FLIGHTS_MIN_LEAD_TIME_DAYS, FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
interface Props {
  optionNumber: number
  offer: App.Offer
  packageView: App.OfferPackageOptionView
  partnership: App.Partnership
  checkIn?: string
  duration: number
  rooms: Array<App.Occupants>
  calendarsLoading?: boolean
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  previewMode: boolean
  currency: string
  baseRatePackagesView: Array<BaseRatePackageView>
  defaultDepositAmountPercentage: number
  canViewLuxPlusBenefits: boolean
  isInclusionsValidityEnabled: boolean
  isFlightsSelected?: boolean
  showOptionLabel?: boolean
  availableForPurchase?: boolean
}

function AlwaysOnRateOptionMobile(props: Props) {
  const {
    offer,
    packageView,
    partnership,
    checkIn,
    duration,
    rooms,
    optionNumber,
    calendarsLoading,
    flightOrigin,
    flightDestination,
    previewMode,
    currency,
    baseRatePackagesView,
    defaultDepositAmountPercentage,
    canViewLuxPlusBenefits,
    isInclusionsValidityEnabled,
    isFlightsSelected,
    showOptionLabel = true,
    availableForPurchase,
  } = props

  const {
    package: pkg,
    flightPrice,
    bundledDiscountPercent,
    hotelDiscountPercent,
  } = packageView

  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const checkOut = useMemo(() => checkOutMoment?.format(ISO_DATE_FORMAT), [checkOutMoment])

  const offerAvailableRates = useOfferAvailableRates(offer.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialOfferPageRooms,
  })

  const pkgAvailableRate = useMemo(() => offerAvailableRates.rates.find(rate => rate.packageUniqueKey === pkg.uniqueKey), [pkg, offerAvailableRates])
  const fetching = !!calendarsLoading || !!offerAvailableRates?.fetching
  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = pkg?.ignoreDynamicCancellationPolicy ?? false
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled

  const dynamicHotelAvailable = ((useDynamicCancellationPolicies && !!pkgAvailableRate && checkIn && !fetching) || !useDynamicCancellationPolicies || !checkIn || fetching)
  const hotelAvailable = packageView.hotelAvailable && dynamicHotelAvailable
  const canPurchase = availableForPurchase && packageView.capacityValid && hotelAvailable
  const canBuyNow = !!checkIn && !!rooms && canPurchase && !fetching && packageView.fromCalendarInfo
  const isValidRoomOccupancy = packageView.flightsAvailable || offer.bundledWithFlightsOnly
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && !(hotelAvailable && !packageView.capacityValid && availableForPurchase && !canPurchase) && isValidRoomOccupancy
  const canAddToUserCart = canBuyNow

  const departureTooEarly = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInMoment])

  const refundable = !useDynamicCancellationPolicies && pkg.roomRate?.cancellationPolicy?.type !== 'non-refundable'

  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS, [checkInMoment])
  const flightsBookable = flightOrigin && flightOrigin.code !== flightDestination?.code && !departureTooEarly

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent
  const shouldShowDiscount = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && (canPurchase || previewMode) && !pkg.roomRate?.inclusionsHideValue

  const baseRatePackageView = useMemo(() => {
    return baseRatePackagesView.find(
      (packageItem) => packageItem.duration === pkg.duration && packageItem.id === pkg.id,
    )
  }, [baseRatePackagesView, pkg.duration, pkg.id])

  const baseRateValue = canViewLuxPlusBenefits ? baseRatePackageView?.memberValue : baseRatePackageView?.value
  const inclusionsAmount = (canViewLuxPlusBenefits ? pkg.totalInclusionsAmount : pkg.inclusionsAmount) ?? 0
  const pkgValueOrPkgMemberValue = canViewLuxPlusBenefits ? pkg.memberValue : pkg.value
  const packageViewHotelValueOrMemberHotelValue = (canViewLuxPlusBenefits ? packageView.hotelMemberValue : packageView.hotelValue) ?? 0

  // We use base rate cost to calculate new totalValue and discountPercent, using the isBaseRate flag on rate plan level.
  // This shows a bigger discount
  const valuedUpTo = useMemo(() => {
    if (baseRateValue && (baseRateValue + inclusionsAmount) > pkgValueOrPkgMemberValue) {
      const displayedHotelValue = baseRateValue + inclusionsAmount + (packageView.propertyFees ?? 0)
      const displayedHotelDiscountPercent = getValueOffPercent(displayedHotelValue, packageView.hotelPrice)

      if (displayedHotelDiscountPercent > highestDiscountPercent) {
        return { value: displayedHotelValue, percent: displayedHotelDiscountPercent }
      }
    }

    return { value: packageViewHotelValueOrMemberHotelValue, percent: highestDiscountPercent }
  }, [baseRateValue, inclusionsAmount, pkgValueOrPkgMemberValue, packageViewHotelValueOrMemberHotelValue, packageView.propertyFees, packageView.hotelPrice, highestDiscountPercent])

  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, packageView.hotelTotal, packageView.package, pkgAvailableRate)

  const { inclusions, bonusInclusions, tacticalInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })
  const hasPackageInclusions = (
    luxPlusInclusions.length > 0 ||
    bonusInclusions.length > 0 ||
    inclusions.length > 0
  )
  const selectedOccupancy = rooms.length === 1 ? rooms[0] : undefined
  const shouldShowInclusionsValidityText = isInclusionsValidityEnabled && pkg.showInclusionsValidityTag && hasPackageInclusions && hasStructuredPackageInclusionsValidityText(pkg.roomPolicyDescription)
  const visibleInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(inclusions, shouldShowInclusionsValidityText)
  }, [inclusions, shouldShowInclusionsValidityText])
  const visibleBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(bonusInclusions, shouldShowInclusionsValidityText)
  }, [bonusInclusions, shouldShowInclusionsValidityText])
  const showPartnership = !!packageView.hotelAvailable && !!partnership?.hasEarn

  const { travelFrom, travelTo } = useMemo(() => {
    const tacticalSchedule = pkg?.roomRate?.schedules?.find(x => x.type === 'tactical')
    let travelFrom = tacticalSchedule?.travelPeriod?.from
    let travelTo = tacticalSchedule?.travelPeriod?.to
    if ((!travelFrom || !travelTo) && tacticalInclusions.length > 0) {
      const tacticalInclusionsSchedule = tacticalInclusions[0].schedules?.find(x => x.type === 'tactical')
      travelFrom = tacticalInclusionsSchedule?.travelPeriod?.from
      travelTo = tacticalInclusionsSchedule?.travelPeriod?.to
    }

    return { travelFrom, travelTo }
  }, [pkg?.roomRate?.schedules, tacticalInclusions])

  const stayPayRateTier = useMemo(() => {
    return pkg?.stayPay?.find(tier => tier.stayNights === duration)
  }, [pkg?.stayPay, duration])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !offer.bundledWithFlightsOnly,
    }),
  ], [offer, packageView, currentRegionCode])

  const handleOpenCalendar = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, pkg.roomType])

  const openCapacityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, pkg.roomType])

  const showHotelAndFlightsToggle = flightsEnabled && !offer.bundledWithFlightsOnly
  const hideHotelPriceBlock = showHotelAndFlightsToggle && isFlightsSelected
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected

  return (
    <AccommodationPackageTile data-testid={pkg.uniqueKey}>
      <AccommodationPackageTileContentArea>
        <AccommodationPackageTileDetails
          packageNumber={showOptionLabel ? optionNumber : undefined}
          roomPackage={packageView.package}
          tagLineLabel={packageView.package.hasTactical && isLPCTacticalEnabled() && <ProductTypeLabel productType="limited_time_special" />}
          stayPayRateTier={stayPayRateTier}
        >
          <Group direction="vertical" gap={8} noDisplayWhenEmpty>
            <Group direction="vertical" gap={4} noDisplayWhenEmpty>
              <TAOCancellationPolicies
                offerId={offer.id}
                useDynamicCancellationPolicies={useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy}
                timezoneOffset={offer.property?.timezoneOffset}
                uniqueKey={pkg.uniqueKey}
                checkInDate={checkIn}
                checkOutDate={checkOut}
                occupants={rooms}
                cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
                offerType={offer.type}
              />
              {!!showPartnership && pkg.partnership &&
                <OfferPackagePartnershipLabel
                  offer={offer}
                  price={packageView.hotelPrice}
                  packagePartnership={pkg.partnership}
                />}
              {packageView.package.hasTactical && isLPCTacticalEnabled() && <BodyText variant="small" weight="normal" colour="light-foreground-alt">
                {[
                  travelFrom && travelTo && intl.formatMessage(
                    {
                      id: 'SXyxxfp36NvLW1uBaVyS',
                      defaultMessage: 'Travel between: {startDate} - {endDate}.',
                      description: 'Travel date range showing start and end dates for the promotional tactical rate on mobile booking',
                    },
                    {
                      startDate: intlDateFormatter(travelFrom, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale),
                      endDate: intlDateFormatter(moment(travelTo).subtract(pkg.duration, 'days'), INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale),
                    },
                  ),
                  intl.formatMessage(
                    {
                      id: 'XHxXe8nyJKqrMiD4Us8F',
                      defaultMessage: 'Minimum stay: {nights, plural, one {# night} other {# nights}}',
                      description: 'Minimum number of nights stay requirement for the promotional tactical rate on mobile booking',
                    },
                    { nights: pkg.duration },
                  ),
                ].filter(Boolean).join(' ')}
              </BodyText>}

              {stayPayRateTier && <BodyText variant="small" colour="normal-contrast">
                <FormattedMessage
                  id="ttXxbsq8vxPjAeWUya11"
                  defaultMessage="Your travel dates qualify for {count, plural, one {# free night} other {# free nights}}"
                  description="Stay-pay promotion message indicating free nights earned"
                  values={{ count: stayPayRateTier.freeNights }}
                />
              </BodyText>}
            </Group>
            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={pointsEarnCalculationRequests}
              calculationType={!checkIn ? 'estimate' : 'exact'}
              offer={offer}
            />
          </Group>
        </AccommodationPackageTileDetails>
        <div>
          <LargeContentToggle
            maxHeight={208}
            showMoreText={intl.formatMessage({
              id: '6tMAFZqZRMMHtdCgUtvo',
              defaultMessage: 'View more inclusions',
              description: 'Toggle text to expand inclusions list for always on rate option mobile',
            })}
            showLessText={intl.formatMessage({
              id: 'oCIxSjDca07WRcDlt0Ph',
              defaultMessage: 'View less inclusions',
              description: 'Toggle text to collapse inclusions list for always on rate option mobile',
            })}
          >
            <RenderInclusions
              bonusInclusions={visibleBonusInclusions}
              inclusions={visibleInclusions}
              luxPlusInclusions={luxPlusInclusions}
              offer={offer}
              stayPayRateTier={stayPayRateTier}
              showReturnFlightsInclusion={!!isFlightsSelected && !!flightsEnabled}
              rooms={rooms}
            />
          </LargeContentToggle>
          {shouldShowInclusionsValidityText && (
            <PackageInclusionsValidityText policyDescription={pkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
          )}
        </div>
      </AccommodationPackageTileContentArea>
      <AccommodationPackageTilePricingArea>
        <WalledContent
          enforced={offer.walledGarden}
          wall={(
            <Group fullHeight direction="vertical" verticalAlign="end">
              <TextButton size="large" kind="secondary" onClick={onSignUpClick}>{intl.formatMessage({
                id: 'LmmQqwMETUdi6PIWkYAe',
                defaultMessage: 'Sign up to view exclusive rates',
                description: 'Button prompting guest to sign up to see exclusive hotel rates',
              })}
              </TextButton>
            </Group>
          )}
        >
          <Group direction="horizontal" gap={4} wrap="wrap">
            {shouldShowDiscount && <PriceRowValueDiscount
              type="estimate"
              discountPercentage={valuedUpTo.percent}
              data-testid="always-on-rate-option-discount"
            />}
            {isDepositEnabled && <SecureWithDepositTagAndModal
              pkgAvailableRate={pkgAvailableRate}
              offerType={offer.type}
              depositAmountPercentage={depositAmountPercentage}
              depositThresholds={offer.depositThresholds}
            />}
          </Group>
          <AccommodationPackageTilePricingBlocks>
            {!offer.bundledWithFlightsOnly && !hideHotelPriceBlock && <AccommodationPackageTilePricingBlock
              data-testid="pricing"
              title={(flightsEnabled && !showHotelAndFlightsToggle) ? ACCOMMODATION_PACKAGE_TITLE.HOTEL : undefined}
              callToAction={<Group direction="vertical" gap={8}>
                {!canBuyNow && !checkIn && <OfferBookButton
                  size="large"
                  type="submit"
                  disabled={fetching}
                  data-testid={clsx('HotelBookingBtn', 'buy-now-button', { refundable })}
                  packageView={packageView}
                >
                  {intl.formatMessage({
                    id: 'AihJYiXRGPjg-pvs1a_l',
                    defaultMessage: 'Check availability',
                    description: 'Button text to check availability on always on rate option mobile',
                  })}
                </OfferBookButton>}
                {canBuyNow && <OfferBookButton
                  size="large"
                  type="submit"
                  disabled={fetching}
                  data-testid={clsx('HotelBookingBtn', 'buy-now-button', { refundable })}
                  packageView={packageView}
                >
                  {intl.formatMessage({
                    id: 'SO7SdtP1AH9jKZbzel6x',
                    defaultMessage: 'Book now',
                    description: 'Button text to book now on always on rate option mobile',
                  })}
                </OfferBookButton>}
                {canAddToUserCart && <OfferAddToCartButton
                  size="large"
                  type="submit"
                  disabled={fetching}
                  packageView={packageView}
                />}
                {availableForPurchase && !canPurchase && <>
                  {hotelAvailable && !packageView.capacityValid && (
                    <MessageBanner
                      kind="informational"
                      icon={<LineExclamationCircleIcon />}
                      heading={intl.formatMessage({
                        id: '-JEBM5sgawi-_x8z2KdT',
                        defaultMessage: 'This room cannot accommodate your number of guests',
                        description: 'Error message heading when room cannot accommodate number of guests',
                      })}
                    >
                      <FormattedMessage
                        id="24d5LxOxd2-_yCEiIrMF"
                        defaultMessage="Choose another room or <link>add more rooms</link>"
                        description="Suggestion to choose another room or add more rooms when capacity is exceeded"
                        values={{
                          link: (chunks) => <TextLink size="medium" weight="regular" onClick={openCapacityFilter}>{chunks}</TextLink>,
                        }}
                      />
                    </MessageBanner>
                  )}
                  {!hotelAvailable && (
                    <MessageBanner
                      kind="informational"
                      icon={<LineExclamationCircleIcon />}
                      heading={intl.formatMessage({
                        id: 'wRhxPTjmfjmvSI9jtb2m',
                        defaultMessage: 'This room is sold out on your dates or max number of guests exceeded',
                        description: 'Error message heading when room is sold out on dates or max number of guests exceeded',
                      })}
                    >
                      <FormattedMessage
                        id="7hXergzCFj7D0sPoE66x"
                        defaultMessage="Choose another room or <link>try different dates</link>"
                        description="Suggestion to choose another room or try different dates when hotel is unavailable"
                        values={{
                          link: (chunks) => <TextLink size="medium" weight="regular" onClick={handleOpenCalendar}>{chunks}</TextLink>,
                        }}
                      />
                    </MessageBanner>
                  )}
                </>}
              </Group>}
            >
              {(canPurchase || previewMode) && <Caption variant="medium">
                <AlwaysOnPriceDetails
                  rooms={rooms}
                  checkIn={checkIn}
                  packageView={packageView}
                  offer={offer}
                  canBuyNow={canBuyNow}
                  valuedUpTo={valuedUpTo}
                />
                <LoadingIndicator visible={fetching} floating opaque />
              </Caption>}
            </AccommodationPackageTilePricingBlock>}
            {flightsEnabled && hotelAvailable && !hideHotelAndFlightsPriceBlock && <AccommodationPackageTilePricingBlock
              data-testid="pricing"
              title={(!offer.bundledWithFlightsOnly && !showHotelAndFlightsToggle) ? ACCOMMODATION_PACKAGE_TITLE.HOTEL_AND_FLIGHTS : undefined}
              callToAction={<Group direction="vertical" gap={8}>
                {!canAddToUserCart && canBuyNow && flightsBookable && (
                  <OfferBookButton
                    size="large"
                    disabled={fetching}
                    data-testid="HotelAndFlightsBtn"
                    data-buytype="flights"
                    packageView={packageView}
                  >
                    {intl.formatMessage({
                      id: 'SO7SdtP1AH9jKZbzel6x',
                      defaultMessage: 'Book now',
                      description: 'Button text to book now on always on rate option mobile',
                    })}
                  </OfferBookButton>
                )}
                {!canBuyNow && !checkIn && <OfferBookButton
                  size="large"
                  disabled={fetching}
                  data-testid="HotelAndFlightsBtn"
                  data-buytype="flights"
                  packageView={packageView}
                >
                  {intl.formatMessage({
                    id: 'AihJYiXRGPjg-pvs1a_l',
                    defaultMessage: 'Check availability',
                    description: 'Button text to check availability on always on rate option mobile',
                  })}
                </OfferBookButton>}
                {canAddToUserCart && flightsBookable && <OfferSelectPackageButton
                  size="large"
                  disabled={fetching}
                  data-buytype="flights"
                  packageView={packageView}
                />}
                {availableForPurchase && !canPurchase && !hotelAvailable && <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'yDnS6fuCFjAq4NZO6RjB',
                    defaultMessage: 'Sold out for your selected dates.',
                    description: 'Error message heading when hotel is sold out for selected dates',
                  })}
                </BodyText>}
                {!hasFlights && flightOrigin.code === flightDestination?.code && <TextButton
                  size="large"
                  data-filter="airport"
                  kind="tertiary"
                  onClick={onToggleFilter}
                >
                  {intl.formatMessage({
                    id: 'zvlHsVNzKnWKIcoJWQSF',
                    defaultMessage: 'Change airport',
                    description: 'Change airport button text for always on rate option mobile',
                  })}
                </TextButton>}
                {flightOrigin.code !== flightDestination?.code && departureTooEarly && <TextButton
                  size="large"
                  data-filter="checkin"
                  kind="tertiary"
                  onClick={onToggleFilter}
                >
                  {intl.formatMessage({
                    id: 'qgkYCP3xXAeKugwGWkMg',
                    defaultMessage: 'Change departure date',
                    description: 'Change departure date button text for always on rate option mobile',
                  })}
                </TextButton>}
              </Group>}
            >
              <VerticalSpacer gap={12}>
                {!hasFlights && flightOrigin.code === flightDestination?.code && <BodyText
                  variant="small"
                  colour="light-foreground-alt"
                >
                  {intl.formatMessage({
                    id: 'PS2YfUA9wOcNa63Egbel',
                    defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
                    description: 'Error message when no flights are available from selected airport',
                  }, { airportName: flightOrigin.name })}
                </BodyText>}
                {!hasFlights && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture && !departureTooEarly && (
                  <BodyText variant="small" colour="light-foreground-alt">
                    {intl.formatMessage({
                      id: '5ujLwTl57ZcN6OMw5Xon',
                      defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
                      description: 'Info text explaining flights can be added to the hotel package',
                    })}
                  </BodyText>
                )}
                {!hasFlights && departureTooFarInFuture && (
                  <BodyText variant="small" colour="light-foreground-alt">
                    {intl.formatMessage({
                      id: 'nW-s6s_wrzcn0c2oITdD',
                      defaultMessage: 'Flights can only be booked up to 11 months in advance.',
                      description: 'Warning that flights cannot be booked more than 11 months ahead',
                    })}
                  </BodyText>
                )}
                {flightOrigin.code !== flightDestination?.code && departureTooEarly && <BodyText
                  variant="small"
                  colour="light-foreground-alt"
                >
                  {intl.formatMessage({
                    id: 'H-dmnQDd0PrQGtb5myyv',
                    defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours.',
                    description: 'Warning that flights are unavailable when check-in is less than 24 hours away',
                  })}
                </BodyText>}
              </VerticalSpacer>
              {((hasFlights && !departureTooEarly) || (hasFlights && previewMode)) && (
                <Caption variant="medium" data-testid="pricing">
                  <AlwaysOnPriceDetails
                    rooms={rooms}
                    checkIn={checkIn}
                    packageView={packageView}
                    offer={offer}
                    canBuyNow={canBuyNow}
                    isFlightPrice
                    valuedUpTo={valuedUpTo}
                  />
                  <LoadingIndicator visible={fetching} floating opaque />
                </Caption>
              )}
              <AtolProtectedTooltip />
            </AccommodationPackageTilePricingBlock>}
          </AccommodationPackageTilePricingBlocks>
          <PriceRowAgentHubCommission
            offerId={offer.id}
            productType={offer.productType}
            offerTotal={packageView.hotelPrice}
            packageId={pkg.id}
          />
        </WalledContent>
      </AccommodationPackageTilePricingArea>
    </AccommodationPackageTile>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Partial<Props>) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  calendarsLoading: !!appState.calendar.calendarsLoading[ownProps.packageView!.package.uniqueKey] || areRatesLoading(appState, state),
  previewMode: appState.system.previewMode,
  flightOrigin: state.airport,
  currency: appState.geo.currentCurrency,
  baseRatePackagesView: getBaseRatePackagesView(appState, state),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isFlightsSelected: state.flights,
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
  isInclusionsValidityEnabled: !!appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croInclusionValidity],
  availableForPurchase: isAvailableForPurchase(state, appState),
}), AlwaysOnRateOptionMobile)
